import { Star } from 'lucide-react';
import type { SupplierProfile } from '../../lib/api';

interface PrimaryContactRowProps {
  supplier: SupplierProfile;
}

/**
 * The "Primary Contact" row on Company Overview — the named person to call
 * about this supplier, as opposed to the company address above it.
 *
 * Read-only. The designation is made in Users Management or from the
 * supplier's Users tab (Edit User Profile), and comes down on the supplier
 * profile payload rather than needing its own request.
 *
 * Extracted from OverviewTab to keep that file within the 500-line limit.
 */
export function PrimaryContactRow({ supplier }: PrimaryContactRowProps) {
  const contact = supplier.primary_contact;

  return (
    <div className="flex items-start gap-3">
      <Star
        className={`h-4 w-4 mt-0.5 ${
          contact ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'
        }`}
      />
      <div className="flex-1">
        <p className="text-sm font-medium">Primary Contact</p>
        {contact ? (
          <>
            <p className="text-sm">
              {contact.name}
              {contact.job_title && (
                <span className="text-muted-foreground">
                  {' · '}{contact.job_title}
                </span>
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {contact.email}
              {contact.phone && ` · ${contact.phone}`}
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Not set — designate one from the Users tab.
          </p>
        )}
      </div>
    </div>
  );
}

export default PrimaryContactRow;
